import { datadogRum } from '@datadog/browser-rum'
import { NfcAuth, NFC_AUTH_API_BASE_URL, VerifyTapResponse } from 'core/api/NfcAuth'
import { DatadogEncodingRun, EncodingValidationRequest, TmrTag } from 'core/api'
import { RfidReader } from 'core/shared'
import { DEVICE_MANAGER_ANTENNA_TIMEOUT } from 'core/shared/RfidReader'
import { __, T } from 'core/shared/i18n'
import {
  CUSTOM_COMMANDS_OPERATION_TYPE,
  decToHex,
  putSpacesEveryByte,
  reverseString,
  sleep,
  urlParamsToObject
} from 'core/shared/utils'

export const EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE = {
  'mediol-40': { inventory: 10, step1: 8, step2: 9, getUid: 15 },
  'mediop-200u': { inventory: 3, step1: 12, step2: 14, getUid: 2 }
} as const

export const SHOULD_REVERSE_TAG_RESPONSES = new Set(['mediop-200u'])

export type NfcAuthProtocol = 'mutualAuth2Step' | 'authNdefRollingCode' | 'no'

export interface NfcAuthOperationOptions {
  nfcAuthProtocol?: NfcAuthProtocol
  nfcAuthApiBaseUrl?: string
  nfcUserMemoryStartBlock?: number
  nfcUserMemoryOffsetBlock?: number
}

export interface ReadNfcTag {
  active: boolean
  block: number
  blockCount: number
}

export const isMutualAuth2StepEnabled = (operation: { options?: NfcAuthOperationOptions }) =>
  operation.options?.nfcAuthProtocol === 'mutualAuth2Step'

export const isAuthNdefRollingCodeEnabled = (operation: { options?: NfcAuthOperationOptions }) =>
  operation.options?.nfcAuthProtocol === 'authNdefRollingCode'

/** True when an NFC auth protocol other than `no` is active. */
export const isNfcAuthEnabled = (operation: { options?: NfcAuthOperationOptions }) =>
  isMutualAuth2StepEnabled(operation) || isAuthNdefRollingCodeEnabled(operation)

export const getNfcReadConfiguration = (operation: { options?: NfcAuthOperationOptions }): ReadNfcTag | undefined => {
  if (!isAuthNdefRollingCodeEnabled(operation)) return undefined
  return {
    active: true,
    block: operation.options?.nfcUserMemoryStartBlock ?? 0,
    blockCount: operation.options?.nfcUserMemoryOffsetBlock ?? 8
  }
}

export const getNfcTagReadErrors = (encodingValidation: EncodingValidationRequest) => ({
  nfcError: encodingValidation.identifiers.some((id) => id.identifierType === 'NFC_TAG' && id._status === 'ERROR'),
  nfcCollision: encodingValidation.identifiers.some(
    (id) => id.identifierType === 'NFC_TAG' && id._status === 'NFC_COLLISION'
  )
})

export const getNfcAuthApiBaseUrl = (operation?: { options?: NfcAuthOperationOptions }) =>
  operation?.options?.nfcAuthApiBaseUrl || NFC_AUTH_API_BASE_URL

export interface NfcMutualAuthSuccessResult {
  finalUid: string
  challengeString?: string
  challengeInHex?: string
}

export type NfcMutualAuthResult = NfcMutualAuthSuccessResult | false | undefined

export const prepareMutualAuthAntenna = async (antennaId: string) => {
  const antennas = RfidReader.getAntennas().filter((a) => a.reader?.settings?.supportedTagType === 'NFC')
  const currentAntenna = antennas.find((a) => a.id === antennaId)
  if (!currentAntenna) return

  const otherAntennasForSameReader = antennas.filter(
    (a) => a.reader.deviceType === currentAntenna.reader.deviceType && a.id !== antennaId
  )
  if (otherAntennasForSameReader.length === 0) return

  RfidReader.excludedAntennaIdsFromStopRequest = otherAntennasForSameReader.map((a) => a.id)
  for (const antenna of [currentAntenna, ...otherAntennasForSameReader]) {
    RfidReader.isManualStop = true
    await RfidReader.getDeviceManagerApi().post(
      `/api/v1/readers/antenna/${antenna.id}/stop?sessionId=${RfidReader.sessionId}`,
      {
        ...currentAntenna.reader,
        id: currentAntenna.reader.code,
        antennas
      },
      { timeout: DEVICE_MANAGER_ANTENNA_TIMEOUT }
    )
  }
  await sleep(200)
  await RfidReader.getDeviceManagerApi().post(
    `/api/v1/readers/antenna/${currentAntenna.id}/start-custom-operation?operationType=${CUSTOM_COMMANDS_OPERATION_TYPE}&sessionId=${RfidReader.sessionId}`,
    {
      ...currentAntenna.reader,
      id: currentAntenna.reader.code,
      antennas: antennas.filter((a) => a.id === currentAntenna.id)
    },
    { timeout: DEVICE_MANAGER_ANTENNA_TIMEOUT }
  )
  RfidReader.resetManualStop(10000)
}

export const parseFinalUid = (readerType: string, finalUidResult: string) =>
  SHOULD_REVERSE_TAG_RESPONSES.has(readerType)
    ? finalUidResult.substring(10, 26)
    : reverseString(finalUidResult.substring(2, 18)).toUpperCase()

export const normalizeTagResponse = (readerType: string, result: string) =>
  SHOULD_REVERSE_TAG_RESPONSES.has(readerType) ? reverseString(result) : result

/** Polls the antenna for a collision-free inventory read, up to 5 attempts. Throws if a collision persists. */
export const checkNfcCollision = async (readerType: string): Promise<void> => {
  let collisionCount = 0
  let collision = true

  while (collisionCount < 5 && collision) {
    const collisionCheck = JSON.parse(
      await RfidReader.sendWsCustomCommandForResponse(
        {
          command: putSpacesEveryByte('260100'),
          parameters: {
            expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].inventory
          }
        },
        false
      )
    )
    if (
      collisionCheck.errorDescription?.toLowerCase().includes('collision') ||
      collisionCheck.result?.toLowerCase().includes('collision')
    ) {
      collisionCount++
      await sleep(500)
    } else {
      collision = false
    }
  }

  if (collision) {
    throw new Error('NFC Collision detected')
  }
}

export const nfcMutualAuthentication2Step = async (
  tag: TmrTag,
  antennaId: string,
  apiBaseUrl: string
): Promise<NfcMutualAuthResult> => {
  const tagId = tag.uid
  if (!tagId) return false

  const dateInMilliseconds = Date.now()
  const dateInHex = decToHex(dateInMilliseconds)
  const challenge = (BigInt(`0x${dateInHex}`) << BigInt(4)).toString(16).toUpperCase()

  const antenna = RfidReader.getAntennas().find((a) => a.id === antennaId)
  if (!antenna) {
    throw new Error('Antenna not found')
  }
  const readerType = antenna.reader.deviceType

  await RfidReader.startWsCustomCommands(antennaId)

  await checkNfcCollision(readerType)

  const step1Res = await RfidReader.sendWsCustomCommandForResponse({
    command: putSpacesEveryByte('02D816038000' + challenge),
    parameters: {
      expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].step1
    }
  })

  const step1ServerRes = await NfcAuth.challenge(
    normalizeTagResponse(readerType, step1Res.result),
    challenge,
    apiBaseUrl
  )

  if (!step1ServerRes) return false

  const connectionId = step1ServerRes?.data?.connectionId ?? ''
  const apdu = step1ServerRes?.data?.APDU ?? ''

  if (!connectionId || !apdu) {
    throw new Error('No connection id or apdu from the server')
  }

  const step2Res = await RfidReader.sendWsCustomCommandForResponse({
    command: putSpacesEveryByte('02D816039000' + apdu),
    parameters: {
      expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].step2
    }
  })

  if ((step2Res.result as string)?.toLowerCase().includes('error')) {
    throw new Error('NFC authentication error')
  }

  if (['010F', '0F01'].includes(step2Res.result)) return false

  await NfcAuth.validate(normalizeTagResponse(readerType, step2Res.result), connectionId, apiBaseUrl)

  // const getCxidCommand = '2023' + tagId + '0404'
  // const getCxidCommand = putSpacesEveryByte('02230404')
  // const cxidRes = await RfidReader.sendWsCustomCommandForResponse({
  //   command: getCxidCommand,
  //   parameters: {
  //     expectedResponseLength: 21,
  //   },
  // })

  // if (!cxidRes.result || cxidRes.result === '000000000000000000000000000000000000000000') return false
  // const finalCxid = splitAndConvertASCII(cxidRes.result)

  const finalUidRes = await RfidReader.sendWsCustomCommandForResponse({
    command: putSpacesEveryByte('022B'),
    parameters: {
      expectedResponseLength: EXPECTED_RESPONSE_LENGTH_BY_READER_TYPE[readerType].getUid
    }
  })

  if ((finalUidRes.result as string)?.toLowerCase().includes('collision')) {
    throw new Error('NFC collision detected')
  }

  const parsedFinalUid = parseFinalUid(readerType, finalUidRes.result)

  return {
    finalUid: parsedFinalUid,
    challengeString: dateInMilliseconds.toString(),
    challengeInHex: challenge
  }
}

export const nfcAuthNdefRollingCode = async (
  tag: TmrTag,
  apiBaseUrl: string
): Promise<NfcMutualAuthSuccessResult | false> => {
  if (!tag.userMemory) return false

  let body: Record<string, string | number>
  try {
    body = urlParamsToObject(tag.userMemory)
  } catch {
    return false
  }

  if (Object.keys(body).length === 0) return false

  const verifyTap: VerifyTapResponse = await NfcAuth.verifyTap(body, apiBaseUrl)
  if (verifyTap.verificationStatus === 'OK' && verifyTap.verificationData?.uid) {
    return { finalUid: verifyTap.verificationData.uid }
  }
  return false
}

const applyNfcAuthResult = (
  encodingValidation: EncodingValidationRequest,
  _tag: TmrTag,
  result: NfcMutualAuthResult,
  datadogEncodingSession?: DatadogEncodingRun
) => {
  if (result === false) {
    encodingValidation.identifiers.forEach((element) => {
      if (element.identifierType === 'NFC_TAG') {
        element._status = 'ERROR'
        element._error = __(T.error.tag_error_or_not_authentic)
      }
    })
    if (datadogEncodingSession) {
      datadogEncodingSession.errors?.push({
        error: 'nfcTagErrorOrNotAuthentic',
        details: { nfcAuthTimeoutOffsetMs: Date.now() - datadogEncodingSession.startTime.getTime() }
      })
      datadogRum.addAction('err_nfcTagErrorOrNotAuthentic', datadogEncodingSession)
    }
  } else if (result === undefined) {
    encodingValidation.identifiers = encodingValidation.identifiers.filter((element) => element.code !== _tag.uid)
  } else {
    _tag.uid = result.finalUid
  }
}

export const readNfcAuthenticatedTag = async (
  encodingValidation: EncodingValidationRequest,
  _tag: TmrTag,
  operationConfig: { options?: NfcAuthOperationOptions },
  antennaId: string,
  datadogEncodingSession?: DatadogEncodingRun
) => {
  const apiBaseUrl = getNfcAuthApiBaseUrl(operationConfig)

  try {
    const res = await Promise.race([
      new Promise<boolean>((_, reject) => {
        setTimeout(() => {
          reject(new Error(__(T.error.authentication_timeout)))
        }, 10000)
      }),
      (async () => {
        let result: NfcMutualAuthResult

        if (isAuthNdefRollingCodeEnabled(operationConfig)) {
          result = await nfcAuthNdefRollingCode(_tag, apiBaseUrl)
        } else {
          await prepareMutualAuthAntenna(antennaId)
          result = await nfcMutualAuthentication2Step(_tag, antennaId, apiBaseUrl)
        }

        applyNfcAuthResult(encodingValidation, _tag, result, datadogEncodingSession)
        return true
      })()
    ])
    return res
  } catch (error: any) {
    console.warn('readNfcAuthenticatedTag error', error)
    if (error.message?.includes('Collision')) {
      await RfidReader.stopWsCustomCommands(false, antennaId)
      if (!encodingValidation.identifiers.some((id) => id.identifierType === 'NFC_TAG')) {
        encodingValidation.identifiers.push({
          code: _tag.uid,
          identifierType: 'NFC_TAG'
        })
      }
      encodingValidation.identifiers.forEach((element) => {
        if (element.identifierType === 'NFC_TAG') {
          element._status = 'NFC_COLLISION'
          element._error = __(T.custom.tooManyTags)
        }
      })
      return false
    }

    encodingValidation.identifiers.forEach((element) => {
      if (element.identifierType === 'NFC_TAG') {
        element._status = 'ERROR'
        element._error = error?.message ? error.message : error
      }
    })
    return false
  }
}
